import axios from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PxGrid from 'components/layout/PxGrid';
import { FormApi } from 'final-form';
import React, { useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, Message } from 'semantic-ui-react';
import { findDoctorById, searchDoctorsForInput } from 'services/doctorServices';
import { getOrderItemTemplateForIntervention } from 'services/interventionOrderItemServices';
import { checkProcedureGroups } from 'services/productServices';
import {
  BlockProcedureExtResponse,
  DoctorDto,
  FieldValidationErrorDto,
  ProductItem,
  ProductItemDto
} from 'ts-types/api.types';
import { required } from 'utils/validatorUtils';

interface Props extends MessageSourceProps {

  onCancel: (form: FormApi) => void,
  onSave: () => void,
  form: FormApi,
  productItemIx: number,
  interventionId: number,
  tariffOptions: DropdownOption[],
  mandateOptions: DropdownOption[],
  procedureGroupOptions: DropdownOption[],
  blockProcedureMap: { [id: number]: BlockProcedureExtResponse }
}

const UpsertProductItemForm = (props: Props) => {

  const [cancelTokenSource/*, setCancelTokenSource*/] = React.useState(axios.CancelToken.source());
  const [pageDataInitialized, setPageDataInitialized] = React.useState(false);
  const [procedureGroupOptions, setProcedureGroupOptions] = useState([] as DropdownOption[])
  const [doctorOptions, setDoctorOptions] = useState([] as DropdownOption[])
  const [componentOptions, setComponentOptions] = useState([] as DropdownOption[])
  const [errorMessages, setErrorMessages] = React.useState([] as FieldValidationErrorDto[]);

  const {
    form,
    productItemIx,
    tariffOptions,
    mandateOptions,
    blockProcedureMap,
    getMessage,
    onCancel,
    onSave,
    getMessageWithNamedParams
  } = props;

  useEffect(() => {
    const procedureGroupOptions = mapBlockProceduresDropdownOptions();
    setProcedureGroupOptions(procedureGroupOptions);
  }, [form.getState().values.productItems[productItemIx].tariffId]);

  useEffect(() => {

    const fetchData = async () => {

      const formValues = form.getState().values;
      if (formValues
          && formValues.productItems
          && (formValues.productItems as [any]).length > productItemIx) {

        const referralDoctorId = formValues.productItems[productItemIx].defaultReferralDoctorId;
        if (referralDoctorId) {
          const doctorDto = await findDoctorById(referralDoctorId, cancelTokenSource);
          if (doctorDto) {
            setDoctorOptions(mapDoctors([{
              id: referralDoctorId,
              ...doctorDto
            }]));
          }
        }
      }

      const interventionId = props.interventionId;
      if (interventionId) {
        const interventionOrderItem =
            await getOrderItemTemplateForIntervention(interventionId, cancelTokenSource);

        if (interventionOrderItem) {
          const availableComponents = [
            ...interventionOrderItem.components,
            ...interventionOrderItem.optionalComponents
          ];

          const componentOptions = mapToDropdownOptionArray(availableComponents, "name");
          setComponentOptions(componentOptions);
        }
      }

      setPageDataInitialized(true);
    }

    fetchData();
  }, []);

  const clearProcedureGroups = (productItem: ProductItem, tariffId: number) => {
    const selectedProcedureGroups = productItem.procedureGroups;

    const filteredBlockProcs: string[] = Object.values(blockProcedureMap)
        .filter(bp => selectedProcedureGroups.includes(bp.description) && bp.tariffs.includes(tariffId))
        .map(bp => bp.description);

    form.change(`productItems[${productItemIx}].procedureGroups`, filteredBlockProcs);
  }

  const mapBlockProceduresDropdownOptions = () => {
    const productItem = form.getState().values.productItems[productItemIx];
    const tariffId = productItem.tariffId;

    clearProcedureGroups(productItem, tariffId);

    return Object.values(blockProcedureMap)
        .filter(bp => bp.tariffs.includes(tariffId))
        .map(bp => ({
          key: bp.key,
          value: bp.description,
          text: bp.description
        }))
  }

  const mapDoctors = (doctors: Array<DoctorDto>): Array<DropdownOption> => {
    return doctors.map(doctor => ({
      key: doctor.id,
      text: `${doctor.name} ${doctor.firstName} - ${doctor.city}`,
      value: doctor.id
    }));
  };

  const searchDoctors = (search: string): Promise<Array<DoctorDto>> => {
    return searchDoctorsForInput(search, cancelTokenSource, 75);
  };

  const onSaveItem = async () => {
    const itemId = form.getFieldState(`productItems[${productItemIx}].id`);
    const mandateId = form.getFieldState(`productItems[${productItemIx}].mandateId`);
    const tariffId = form.getFieldState(`productItems[${productItemIx}].tariffId`);
    const procedureGroups = form.getFieldState(`productItems[${productItemIx}].procedureGroups`);
    const errors =
        [itemId!.error, mandateId!.error, tariffId!.error, procedureGroups!.error];

    if (!errors.includes("Required")) {
      const productItem = form.getState().values.productItems[productItemIx];
      const request: ProductItemDto = {
        procedureGroups: productItem.procedureGroups,
        tariffId: productItem.tariffId
      }
      const errors = await checkProcedureGroups(request, cancelTokenSource);
      if (!errors.length) {
        onSave()
      } else {
        setErrorMessages(errors);
      }
    }
  }

  let disableProcedureGroups = false;
  const formValues = form.getState().values;
  const productItem = formValues.productItems[productItemIx];
  if (productItem) {
    disableProcedureGroups = !productItem.tariffId;
  }

  const renderErrors = () => {
    return errorMessages.length
        ? <Grid.Row>
          <Grid.Column width={16}>
            <Message
                error
                header={getMessage("product.form.item.error.header")}
                list={errorMessages.map(error => getMessageWithNamedParams(
                        `product.form.item.error.${error.errorCode}`,
                        {
                          procedureGroup: error.field
                        }
                    )
                )}
            />
          </Grid.Column>
        </Grid.Row>
        : <></>
  }

  if (!pageDataInitialized) {
    return <></>;
  }

  return (
      <PxGrid>
        {renderErrors()}

        <Grid.Row>
          <Grid.Column>
            <Header size="small">{getMessage("product.form.item.title")}</Header>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={3}>
            {getMessage("product.form.item.lineItem")}
          </Grid.Column>
          <Grid.Column width={5}>
            <Field
                fluid
                name={`productItems[${productItemIx}].id`}
                component={Input}
                validate={required}
                autoFocus
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={3}>
            {getMessage("product.form.item.invoiceMandate")}
          </Grid.Column>
          <Grid.Column width={7}>
            <Field
                fluid
                name={`productItems[${productItemIx}].mandateId`}
                component={Select}
                options={mandateOptions}
                clearable
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={3}>
            {getMessage("product.form.item.tariff")}
          </Grid.Column>
          <Grid.Column width={7}>
            <Field
                fluid
                name={`productItems[${productItemIx}].tariffId`}
                component={Select}
                options={tariffOptions}
                validate={required}
            />
          </Grid.Column>
        </Grid.Row>

        {
            props.interventionId &&
            <Grid.Row>
              <Grid.Column width={3}>
                {getMessage("product.form.item.component")}
              </Grid.Column>
              <Grid.Column width={7}>
                <Field
                    fluid
                    name={`productItems[${productItemIx}].componentId`}
                    component={Select}
                    options={componentOptions}
                    clearable
                    // validate={required}
                />
              </Grid.Column>
            </Grid.Row>
        }

        <Grid.Row>
          <Grid.Column width={3}>
            {getMessage("product.form.item.procedureGroups")}
          </Grid.Column>
          <Grid.Column width={7}>
            <Field
                fluid
                name={`productItems[${productItemIx}].procedureGroups`}
                component={Select}
                validate={required}
                options={procedureGroupOptions}
                disabled={disableProcedureGroups}
                searchable={true}
                multiple
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={3}>
          </Grid.Column>
          <Grid.Column width={7}>
            <Field
                name={`productItems[${productItemIx}].cashOptions`}
                label={getMessage("product.form.item.cashOptions")}
                component={CheckBox}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={3}>
            {getMessage("treatmentForm.referral")}
          </Grid.Column>
          <Grid.Column width={7}>
            <Field
                fluid
                name={`productItems[${productItemIx}].defaultReferralDoctorId`}
                component={Select}
                placeholder={getMessage('doctor')}
                options={doctorOptions}
                remoteSearchMethod={searchDoctors}
                searchResultToOptionMapper={mapDoctors}
                remoteSearch
                upward
                clearable
            />
          </Grid.Column>
        </Grid.Row>

        <FormButtonsRow>
          <Button compact primary onClick={() => onSaveItem()}>{getMessage("button.save")}</Button>
          <Button compact onClick={() => onCancel(form)}>{getMessage("button.cancel")}</Button>
        </FormButtonsRow>
      </PxGrid>
  );
}

export default withMessages(UpsertProductItemForm);
